import React, { useEffect, useState } from 'react';
import { FileText, Trash2, Download, Upload } from 'lucide-react';

type FileType = {
  id: string;
  name: string;
  created_at: string;
  size: number;
  type: string;
  url: string;
};

interface MyFilesProps {
  files: Array<{ id: string; name: string; webViewLink: string }>;
}

export function MyFiles({ files: propFiles }: MyFilesProps) {
  const [files, setFiles] = useState<FileType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFiles();
  }, [propFiles]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load files from localStorage for offline mode
      const savedFiles = localStorage.getItem('user_files');
      if (savedFiles) {
        const parsedFiles = JSON.parse(savedFiles);
        setFiles(parsedFiles);
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Error al cargar archivos');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const newFile: FileType = {
      id: Date.now().toString(),
      name: file.name,
      created_at: new Date().toISOString(),
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file)
    };

    const updatedFiles = [...files, newFile];
    setFiles(updatedFiles);
    localStorage.setItem('user_files', JSON.stringify(updatedFiles));

    // Clear the input
    event.target.value = '';
  };

  const handleDelete = async (fileId: string) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este archivo?')) {
      return;
    }

    try {
      const updatedFiles = files.filter(f => f.id !== fileId);
      setFiles(updatedFiles);
      localStorage.setItem('user_files', JSON.stringify(updatedFiles));
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Error al eliminar archivo');
    }
  };

  const handleDownload = (file: FileType) => {
    const element = document.createElement('a');
    element.href = file.url;
    element.download = file.name;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Mis Archivos</h1>
        
        <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer transition-colors duration-200">
          <Upload className="w-4 h-4 mr-2" />
          Subir Archivo
          <input
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
          />
        </label>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay archivos</h3>
          <p className="mt-1 text-sm text-gray-500">
            Los archivos que subas aparecerán aquí.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Archivos Subidos ({files.length})
            </h3>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {files.map((file) => (
                <li key={file.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors duration-150">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownload(file)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors duration-150"
                        title="Descargar"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="text-red-400 hover:text-red-600 p-1 rounded transition-colors duration-150"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}